import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faComment } from '@fortawesome/free-regular-svg-icons';
import { faCircle, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import styled from 'styled-components';
import useTracking from '../../hooks/useTracking';
import { AQUAMARINE, BLACK2, BOX_SHADOW_3, WHITE } from '../../theme';
import {
  EventCategories,
  EventNames,
  EventSubCategories,
} from '../../utils/trackingEvents';
import { ButtonVariant } from './Button';
import { IconButton } from './Button/IconButton';
import { IconStack } from './IconStack';

const StyledFloatingButton = styled.div<{ $position: Position }>(
  ({ $position }) => ({
    position: 'fixed',
    bottom: '24px',
    left: $position === 'left' ? '24px' : 'auto',
    right: $position === 'right' ? '24px' : 'auto',
    borderRadius: '99ch',
    boxShadow: BOX_SHADOW_3,
  }),
);

const FadeIn = styled.div<{
  $show: boolean;
  $reverse: boolean;
}>`
  opacity: ${({ $show }) => ($show ? 1 : 0)};

  ${({ $reverse, $show }) =>
    $reverse
      ? `
    transform: ${$show ? 'rotate(0)' : 'rotate(180deg)'};
    `
      : `
    transform: ${$show ? 'rotate(180deg)' : 'rotate(0)'};
    `}

  transition:
    opacity ease-in-out 300ms,
    transform ease-in-out 300ms;
`;

type Position = 'left' | 'right';

interface FloatingButtonProps {
  open: boolean;
  onClick: () => void;
  icon?: IconProp;
  position?: Position;
  showUnreadIcon?: boolean;
  testId?: string;
  trackingOpenEvent: EventNames;
  trackingCategory: EventCategories;
  trackingSubCategory: EventSubCategories;
  trackingMemberId?: string;
}

export const FloatingButton: React.FC<FloatingButtonProps> = ({
  onClick,
  open,
  icon = faComment,
  position = 'right',
  showUnreadIcon = false,
  testId = 'FloatingButton',
  trackingOpenEvent,
  trackingCategory,
  trackingSubCategory,
  trackingMemberId,
}) => {
  const { track } = useTracking();

  const handleClick = () => {
    if (open) {
      track(EventNames.CloseClick, {
        event_category: trackingCategory,
        event_subcategory: trackingSubCategory,
        member_id: trackingMemberId,
      });
    } else {
      track(trackingOpenEvent, {
        event_category: trackingCategory,
        event_subcategory: trackingSubCategory,
        member_id: trackingMemberId,
      });
    }

    if (onClick) {
      onClick();
    }
  };

  return (
    <StyledFloatingButton $position={position}>
      <IconButton
        variant={ButtonVariant.Dark}
        testId={testId}
        onClick={handleClick}
        icon={
          <IconStack>
            <FadeIn $show={open} $reverse={false}>
              <FontAwesomeIcon
                data-testid="FloatingButton-closeIcon"
                icon={faTimes}
                color={WHITE}
                fontSize={20}
              />
            </FadeIn>
            <FadeIn $show={!open} $reverse>
              <FontAwesomeIcon
                data-testid="FloatingButton-openIcon"
                icon={icon}
                color={WHITE}
                fontSize={20}
              />
              {showUnreadIcon && !open && (
                <FontAwesomeIcon
                  data-testid="FloatingButton-unreadIndicatorIcon"
                  icon={faCircle}
                  fontSize={8}
                  color={AQUAMARINE}
                  style={{
                    position: 'absolute',
                    top: -2,
                    right: -4,
                    borderRadius: '99ch',
                    border: `2px solid ${BLACK2}`,
                  }}
                />
              )}
            </FadeIn>
          </IconStack>
        }
      />
    </StyledFloatingButton>
  );
};
